/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Short chime when an inbound WhatsApp message arrives.
 *
 * Mirrors telephony/ringer.js browser-audio rules:
 * - Chrome blocks audio until a user gesture; prime() unlocks from first click.
 * - Prefer a shipped asset when present; synthesise a soft two-tone ping otherwise
 *   so agents still hear something before anyone adds a wav.
 * - One-shot (not looped). Debounced so a burst of webhook events does not
 *   fire a machine-gun of pings.
 */

const MIN_INTERVAL_MS = 1500;

let element = null;
let audioContext = null;
let unlocked = false;
let lastPlayedAt = 0;
let gestureBound = false;
/** Survives DashboardNavbar remounts so the same WS seq does not re-chime. */
let lastChimedSeq = 0;
/** null until probed; false when the wav is missing so we never 404 again. */
let wavAvailable = null;

function getElement() {
  if (wavAvailable === false) return null;
  if (element) return element;
  if (typeof Audio === "undefined") return null;
  // Asset is optional — only construct Audio after a HEAD probe succeeds.
  // Until then prefer the synthesised ping so missing /sounds/whatsapp-notify.wav
  // does not spam 404s in the network panel.
  if (wavAvailable !== true) return null;
  element = new Audio("/sounds/whatsapp-notify.wav");
  element.preload = "auto";
  element.volume = 0.55;
  return element;
}

function probeWav() {
  if (wavAvailable !== null || typeof fetch === "undefined") return;
  wavAvailable = false; // optimistic: avoid concurrent probes
  fetch("/sounds/whatsapp-notify.wav", { method: "HEAD", cache: "force-cache" })
    .then((res) => {
      wavAvailable = res.ok;
    })
    .catch(() => {
      wavAvailable = false;
    });
}

function ensureContext() {
  if (typeof window === "undefined" || !window.AudioContext) return null;
  if (!audioContext) {
    try {
      audioContext = new window.AudioContext();
    } catch {
      return null;
    }
  }
  if (audioContext.state === "suspended") {
    audioContext.resume().catch(() => {});
  }
  return audioContext;
}

/** Soft WhatsApp-like ping when the asset is missing or blocked. */
function playSynthesized() {
  const ctx = ensureContext();
  if (!ctx) return;

  const now = ctx.currentTime;
  [0, 0.12].forEach((offset, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = i === 0 ? 880 : 1175;
    const startAt = now + offset;
    gain.gain.setValueAtTime(0, startAt);
    gain.gain.linearRampToValueAtTime(0.08, startAt + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.001, startAt + 0.22);
    osc.connect(gain).connect(ctx.destination);
    osc.start(startAt);
    osc.stop(startAt + 0.25);
  });
}

/**
 * Unlock audio from a user gesture (any click / key / touch after login).
 */
export function prime() {
  probeWav();
  const el = getElement();
  if (el && !unlocked) {
    el.play()
      .then(() => {
        el.pause();
        el.currentTime = 0;
        unlocked = true;
      })
      .catch(() => {
        /* still locked; synthesised path may work after gesture */
      });
  }
  ensureContext();
  unlocked = true;
}

/** Bind once so the first interaction anywhere in the app unlocks audio. */
export function bindAutoPrime() {
  if (gestureBound || typeof document === "undefined") return;
  gestureBound = true;
  const unlock = () => {
    prime();
    document.removeEventListener("pointerdown", unlock, true);
    document.removeEventListener("keydown", unlock, true);
  };
  document.addEventListener("pointerdown", unlock, true);
  document.addEventListener("keydown", unlock, true);
}

/**
 * Play the inbound WhatsApp notification chime.
 * Returns false when skipped (debounced) or audio unavailable.
 */
export function play() {
  const now = Date.now();
  if (now - lastPlayedAt < MIN_INTERVAL_MS) return false;
  lastPlayedAt = now;

  probeWav();
  const el = getElement();
  if (el) {
    try {
      el.currentTime = 0;
      const played = el.play();
      if (played && typeof played.catch === "function") {
        played.catch(() => playSynthesized());
      }
      return true;
    } catch {
      playSynthesized();
      return true;
    }
  }

  playSynthesized();
  return true;
}

/**
 * True when the WS inbox event represents newly stored / queued inbound work
 * (not outbound send, read, claim, or delivery-status-only updates).
 */
export function isInboundWhatsAppEvent(event) {
  if (!event) return false;
  const payload = event.payload || event;
  const stored = Number(payload.stored) || 0;
  const enqueued = Number(payload.enqueued) || 0;
  return stored > 0 || enqueued > 0;
}

/**
 * Play at most once per inbound event seq. Module-level so navbar remounts
 * (route changes / token refresh) do not replay the same event.
 * Returns true when a chime was started.
 */
export function playInboundOnce(event) {
  if (!isInboundWhatsAppEvent(event)) return false;
  const seq = Number(event?.seq) || 0;
  if (seq && seq === lastChimedSeq) return false;
  if (seq) lastChimedSeq = seq;
  return play();
}
